import type { MealItem } from '../api/types';
import {
  formatEstimateKcal,
  formatGrams,
  isUncertain,
  itemStateLabel,
  itemTitle,
  unitLabel,
} from '../format/nutrition';
import { ConfidenceMeter, Spinner } from './index';
import { ItemThumb } from './MediaThumb';

/**
 * Una fila de ítem dentro de una comida.
 *
 * Es una fila y no una tarjeta con botones: en la pantalla de hoy hay seis de estas y cada botón
 * repetido seis veces es ruido. Todo lo que se puede hacer con un ítem vive en su detalle; acá
 * sólo se lee qué es, cuánto vale y cuánta certeza tiene ese número.
 */
export function ItemRow({
  mealId,
  item,
  onOpen,
  justResolved = false,
}: {
  mealId: string;
  item: MealItem;
  onOpen: () => void;
  justResolved?: boolean;
}) {
  const estimate = item.currentEstimate;

  const meta = [
    item.quantity !== null && item.unit !== null
      ? `${formatGrams(item.quantity)} ${unitLabel(item.unit)}`
      : null,
    estimate === null ? itemStateLabel(item.state) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  /*
   * La fila cuya cifra salió de una foto se tiñe con el tono de marca.
   *
   * No es una advertencia —el naranja nunca lo es en este sistema— sino la respuesta a «¿de dónde
   * salió este número?». Es el cuarto canal del indicador de confianza, después del ancho, el peso
   * y la palabra: quien recorre el día ve de un vistazo qué filas son medición y cuáles lectura.
   */
  const estimated = estimate !== null && isUncertain(estimate);

  const classes = ['item-row', estimated ? 'item-row-estimated' : '', justResolved ? 'item-row-resolved' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={classes} onClick={onOpen}>
      <ItemThumb
        mealId={mealId}
        itemId={item.id}
        media={item.media[0] ?? null}
        ean={item.ean}
        alt={itemTitle(item)}
      />

      <span className="item-body">
        <span className="item-name">{itemTitle(item)}</span>
        {meta !== '' && <span className="item-meta">{meta}</span>}
      </span>

      <span className="item-value">
        {estimate === null ? (
          item.state === 'Pending' ? (
            <Spinner label="Estimando" />
          ) : (
            <span className="item-kcal uncertain">—</span>
          )
        ) : (
          <>
            <span className={isUncertain(estimate) ? 'item-kcal uncertain' : 'item-kcal'}>
              {formatEstimateKcal(estimate)}
            </span>
            <ConfidenceMeter source={estimate.source} confidence={estimate.confidence} />
          </>
        )}
      </span>
    </button>
  );
}
